/**
 * usePWCurrentLogic.js
 * React hook for Pregnant Women Current Vaccination scenario
 *
 * Phase 1: Custom hook that encapsulates PW Current vaccination logic
 * Returns stable DoseConfig contract for UI components
 */

import { useMemo } from 'react';
import { getPWCurrentDoseStatus } from './PWCurrentRules';

/**
 * Custom hook for PW Current vaccination logic
 * Provides stable contract interface for UI components
 * @param {Object} vaccine - Vaccine object with id, name, availableDoses
 * @param {Object} profileData - Profile data with lmc_date and vaccination history
 * @param {Function} decryptFunction - Function to decrypt dose numbers
 * @returns {Object} - Hook result with getDoseConfig function
 */
export const usePWCurrentLogic = (vaccine, profileData, decryptFunction, identityContext = null) => {
  // Memoize the dose config calculation to prevent unnecessary re-renders
  const getDoseConfig = useMemo(() => {
    return (doseIndex) => {
      try {
        // Get dose status using pure business logic
        const doseStatus = getPWCurrentDoseStatus(
          vaccine,
          doseIndex,
          profileData,
          decryptFunction,
          identityContext
        );

        // Return stable DoseConfig contract
        return {
          // Core properties for UI rendering
          enabled: doseStatus.enabled || false,
          showRadio: doseStatus.showRadio || false,
          status: doseStatus.status || 'Unknown',

          // Optional properties
          date: doseStatus.date || null,
          reason: doseStatus.reason || null,
          isCalculated: doseStatus.isCalculated || false,

          // Metadata for debugging/logging
          scenario: 'pw-current',
          vaccineId: vaccine.id,
          vaccineName: vaccine.name,
          doseIndex
        };
      } catch (error) {
        console.error('Error in usePWCurrentLogic.getDoseConfig:', error);

        // Return safe fallback state
        return {
          enabled: false,
          showRadio: false,
          status: 'Error',
          date: null,
          reason: 'Calculation error',
          isCalculated: false,
          scenario: 'pw-current',
          vaccineId: vaccine.id,
          vaccineName: vaccine.name,
          doseIndex
        };
      }
    };
  }, [vaccine, profileData, decryptFunction, identityContext]);

  // Return stable hook interface
  return {
    getDoseConfig,
    scenario: 'pw-current',
    isReady: !!(vaccine && profileData && decryptFunction)
  };
};

/**
 * Higher-order hook for PW Current vaccination with additional features
 * Can be extended with validation, caching, or other enhancements
 * @param {Object} vaccine - Vaccine object
 * @param {Object} profileData - Profile data
 * @param {Function} decryptFunction - Decrypt function
 * @param {Object} options - Additional options for future extension
 * @returns {Object} - Enhanced hook result
 */
export const usePWCurrentLogicEnhanced = (
  vaccine,
  profileData,
  decryptFunction,
  identityContext = null,
  options = {}
) => {
  const baseLogic = usePWCurrentLogic(vaccine, profileData, decryptFunction, identityContext);

  // Destructure stable refs BEFORE useMemo so the dependency array only holds
  // primitive / referentially-stable values, not the whole baseLogic object.
  // If baseLogic were listed directly, a new object reference each render would
  // cause allDoseConfigs to recompute on every render even when nothing changed.
  const { getDoseConfig, isReady } = baseLogic;

  // Calculate all dose configs at once for performance
  const allDoseConfigs = useMemo(() => {
    if (!isReady || !vaccine.availableDoses) {
      return [];
    }

    return vaccine.availableDoses.map((dose, index) =>
      getDoseConfig(index)
    );
  }, [getDoseConfig, isReady, vaccine.availableDoses]);

  return {
    ...baseLogic,
    allDoseConfigs,

    // Convenience methods
    isDoseEnabled: (doseIndex) => {
      return allDoseConfigs[doseIndex]?.enabled || false;
    },

    getDoseStatus: (doseIndex) => {
      return allDoseConfigs[doseIndex]?.status || 'Unknown';
    },

    hasAdministeredDoses: allDoseConfigs.some(config => config.status === 'administered'),
    hasEligibleDoses: allDoseConfigs.some(config => config.status === 'Eligible'),

    // Debug info
    debugInfo: options.debug ? {
      vaccine: vaccine.name,
      lmcDate: profileData?.lmc_date,
      vaccinationCount: profileData?.vaccination?.length || 0
    } : null
  };
};